import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { AGENT_SAVE_CACHE_KEY, useUpdateAgentMutation } from '../store/agents-api.js';
import { GREETING_SAVE_CACHE_KEY, useUpdateGreetingMutation } from '../store/settings-api.js';
import { useGetMeQuery, useLogoutMutation } from '../store/auth-api.js';
import { useGetQueueQuery } from '../store/contacts-api.js';
import { BrandLogo, WordMark } from '../ui/BrandLogo.js';
import { IconLogout } from '../ui/icons.js';
import { PlanetBurger } from '../ui/PlanetBurger.js';
import { useAppDispatch } from '../store/hooks.js';
import { pushToast } from '../ui/toast-store.js';
import { NotificationBell } from '../features/notifications/NotificationBell.js';
import { TestToolsCorner } from '../features/maintenance/TestToolsCorner.js';
import { ConnectionStatus } from './ConnectionStatus.js';
import { NAV_ITEMS, ROUTE_TITLES } from './nav.js';
import styles from './AppShell.module.css';

/**
 * The persistent app shell (S03).
 *
 * Sidebar, top bar and outlet. Rendered only for a signed-in user — the route
 * guard above it handles the redirect — so `me` is present by the time this
 * mounts and the identity block never flickers through an empty state.
 */
export function AppShell(): JSX.Element {
  const { data: me } = useGetMeQuery();
  const [logout, { isLoading: signingOut }] = useLogoutMutation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  // Falls back to the section, so a nested route keeps its section's heading:
  // `/agents/<id>` is still Agent Configuration, and without this the top bar
  // would read "Voice Agent Console" the moment an agent was opened.
  const section = `/${pathname.split('/')[1] ?? ''}`;
  const [title, subtitle] = ROUTE_TITLES[pathname] ??
    ROUTE_TITLES[section] ?? ['Voice Agent Console', ''];

  // Below 900px the sidebar leaves the flow and becomes a drawer. The state lives
  // here because the control that opens it and the panel it opens are siblings —
  // neither owns the other.
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstLink = useRef<HTMLAnchorElement>(null);

  // Navigating is the whole point of the drawer, so arriving somewhere closes
  // it. Keyed on pathname so it also closes when a link points at the current
  // route and nothing else changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    firstLink.current?.focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);

    // Without this the page scrolls behind the scrim, which makes the drawer
    // feel like a floating panel rather than a layer over a held page.
    document.body.classList.add('menu-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('menu-open');
      // The button that opened it is where focus belongs on the way out; the
      // link that had focus may no longer be on screen.
      toggleRef.current?.focus();
    };
  }, [menuOpen]);

  // S06: "the queue persists and drives the sidebar badge". Null until the
  // first response, because rendering 0 would assert an empty queue we have not
  // looked at yet; the badge appears when there is something to say.
  const { data: queue } = useGetQueueQuery();
  const queueCount: number | null = queue ? queue.count : null;

  /**
   * Whether a configuration save is in flight, so the mark can work while it is.
   *
   * Both hooks are called for their state, not their triggers: the fixed cache
   * keys make these the same mutations the agent editor started, which is how the
   * sidebar knows without a global busy flag that every future feature would have
   * to remember to set and clear. The turning ring already means "waiting" on the
   * login card; this is the same motion for the same reason.
   *
   * The two are one signal here because they are one gesture to an operator —
   * saving what the agent says. That they reach different endpoints is our
   * problem, not theirs.
   */
  const [, agentSave] = useUpdateAgentMutation({ fixedCacheKey: AGENT_SAVE_CACHE_KEY });
  const [, greetingSave] = useUpdateGreetingMutation({ fixedCacheKey: GREETING_SAVE_CACHE_KEY });
  const savingConfig = agentSave.isLoading || greetingSave.isLoading;

  async function onSignOut(): Promise<void> {
    try {
      const result = await logout().unwrap();
      // Sign out of the IdP too when it supports it — Schwarz's does, which is
      // why deviation D1 shrank. Full navigation, since we are leaving the SPA.
      // With federated sign-out the IdP takes over. Locally we land on our own
      // login page, and say so there: the Schwarz session is still open, which
      // is why signing in again will not ask for anything.
      window.location.assign(
        result.endSessionUrl ?? (result.ssoStillActive ? '/login?signedOut=sso' : '/login'),
      );
    } catch {
      dispatch(pushToast('error', 'Could not sign out cleanly. Please close your browser.'));
    }
  }

  return (
    <div className={styles.layout}>
      {/* Hoisted out of the top bar so it can outrank the drawer and sit on it.
          Inside the bar it could not: .topbar carries a backdrop-filter, which
          makes it a stacking context, so a z-index there orders the button
          against its siblings in the bar and no higher. Fixed and pinned, it
          still reads as part of the bar — the bar just leaves room for it.

          Hidden above 900px, where the sidebar is permanent. */}
      <button
        type="button"
        ref={toggleRef}
        className={`${styles.toggle} ${menuOpen ? styles.toggleOpen : ''}`}
        onClick={() => setMenuOpen((open) => !open)}
        aria-expanded={menuOpen}
        aria-controls="app-nav"
        aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
        title={menuOpen ? 'Close navigation' : 'Open navigation'}
      >
        <PlanetBurger open={menuOpen} />
      </button>

      {/* Only rendered while open, and display:none above 900px, so the desktop
          layout can never end up with an invisible click-blocker over it. */}
      {menuOpen && (
        <div className={styles.scrim} onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}

      <aside id="app-nav" className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
        {/* Two halves of one lockup, and only ever one of them on screen. Above
            900px the sidebar shows the whole thing. Below it, the mark is the
            toggle floating over this panel and only the words belong here, at the
            same size and the same distance from the mark as when it is docked —
            see --brand-x. They arrive while the mark is still solidifying, so the
            name reads as part of the same gesture rather than a second event. */}
        <div className={styles.brand}>
          <BrandLogo size="sidebar" maskId="ps-mark-sidebar" busy={savingConfig} />
        </div>
        <WordMark size="sidebar" className={styles.drawerWord} />

        <nav aria-label="Main">
          <p className={styles.navLabel}>Operate</p>
          {NAV_ITEMS.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              ref={index === 0 ? firstLink : undefined}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <item.Icon />
              <span
                className={`${styles.navText} ${
                  item.queueBadge && (queueCount ?? 0) > 999 ? styles.navTextWrap : ''
                }`}
              >
                {item.label}
              </span>
              {item.queueBadge && queueCount !== null && (
                <span className={styles.pill}>{queueCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className={styles.foot}>
          <div className={styles.avatar} aria-hidden="true">
            {initials(me?.name ?? me?.email ?? '')}
          </div>
          {/* The verified identity S03 asks for: this comes from the IdP, not
              from anything the user typed. The title carries the address, which the
              mock shows nowhere but is worth keeping one hover away. */}
          <div className={styles.who} title={me?.email}>
            <b>{me?.name ?? me?.email ?? '—'}</b>
            {/* Cosmetic, matching the mock. There are no roles in this build —
                per Q11 every allowlisted user sees everything — so this labels the
                product's single persona rather than asserting a permission level. */}
            <span>Operator</span>
          </div>
          <button
            type="button"
            className={styles.signOut}
            onClick={() => void onSignOut()}
            disabled={signingOut}
            // `title` is the visible tooltip; `aria-label` is the accessible
            // name. Both, because `title` sits last in the accessible-name order
            // and is not reliably announced.
            //
            // An aria-label rather than a visually-hidden span: `.sr-only` is
            // absolutely positioned, so it resolves against the nearest
            // positioned ancestor — `#root` — and escapes any scroll container in
            // between. Inside the Access table that put invisible 1px boxes
            // hundreds of pixels past the viewport and gave the whole page a
            // horizontal scrollbar. An attribute has no box and cannot.
            title="Sign out"
            aria-label="Sign out"
          >
            <IconLogout width="17" height="17" />
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.heading}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.crumb}>{subtitle}</p>}
          </div>
          <div className={styles.topbarRight}>
            <ConnectionStatus />
            <NotificationBell />
          </div>
        </header>

        {/* Scrolls inside the container, never the page — S03's requirement that
            wide tables scroll within their own container. */}
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>

      {/* Test environments only. The server decides: where the tools are off,
          `testTools` is false and there is nothing in the corner to find. */}
      {me?.testTools === true && <TestToolsCorner />}
    </div>
  );
}

function initials(source: string): string {
  const parts = source
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
